import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
// MemoryRouter keeps all navigation state in memory — the browser URL
// never changes from `/`. Matches FinComp's no-path feel. Trade-off:
// bookmark/share don't preserve the current page. It starts at the address
// the browser loaded (consumeBootUrl), or after a reload at the page
// HistoryBridge stored in history.state, so F5 keeps the page and an emailed
// /reset-password?token=... link opens the reset form, and then puts the
// address bar back to `/` so the token does not linger there or in history.
import { MemoryRouter } from 'react-router-dom';
import App from './App';
import { consumeBootUrl, restoreBootUrl } from './bootEntry';
import { BootRouteTracker } from './BootRouteTracker';
import './index.css';
// Our own generated icon webfont for the MAIN nav module glyphs (sources in
// web/icon-src, compiled via `npm run icons` / fantasticon; no SVG, no CDN —
// vite bundles the css + woff2 under the app origin, satisfying the CSP).
import './icon-font/fritech-icons.css';
import { initAppearance } from './features/settings/AppearancePage';
import { startTabSessionGuard } from './shared/tabSessionGuard';

initAppearance();

// PWA self-healing: when a NEW service worker takes control mid-session (a
// deploy happened), reload once so the user gets the fresh bundle immediately
// instead of staying one build behind until a second manual refresh. The
// hadController guard skips the very first install (fresh visitor) and
// prevents reload loops. Form state survives via the autosave/scratchpad
// conventions.
if ('serviceWorker' in navigator) {
  let hadController = !!navigator.serviceWorker.controller;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    // The address bar reads "/" by now; put the boot address back first so
    // the reload reopens it (an emailed reset link, /login?reason=idle), but
    // only while the user is still on that route (restoreBootUrl checks).
    if (hadController) { restoreBootUrl(); window.location.reload(); }
    else hadController = true;
  });
}

// Tab-close policy: when the app starts with no other live tab (and isn't a
// same-tab reload), kill the server session BEFORE first render so the auth
// check lands on the login screen. Reloads and additional tabs continue.
const tabVerdict = startTabSessionGuard();

// Read once, before anything renders: the address bar goes back to `/` here.
const bootUrl = consumeBootUrl();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      // Release unused page caches sooner than the 5-min default — a long
      // session touring many list pages otherwise accumulates a copy of every
      // visited list in the heap.
      gcTime: 2 * 60_000,
    },
  },
});

async function boot() {
  if (tabVerdict === 'fresh-start') {
    try { await fetch('/api/v1/auth/logout', { method: 'POST', credentials: 'same-origin' }); } catch { /* no session — fine */ }
  }
  createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <MemoryRouter initialEntries={[bootUrl]}>
        <BootRouteTracker />
        <App />
      </MemoryRouter>
    </QueryClientProvider>
  </StrictMode>,
);
}
void boot();
